import { AirshipHangarPoint } from "../types/airship"
import { BossPointView } from "../types/boss"
import { BankPoint } from "../types/bank"
import { HotelPoint } from "../types/hotel"
import { WiseTreePoint } from "../types/wiseTree"
import { CardShopPoint } from "../types/cardShop"
import { BoosterShopPoint } from "../types/boosterShop"
import { PostOfficePoint } from "../types/mail"
import { LocationPoint, PointHistory, WorldMapPointKind } from "../types/location"
import { TreasurePoint } from "../types/treasure"
import { WorldBossEventView } from "../types/worldBoss"

export interface WorldMapTreasureMeta {
  mode: "treasure" | "starfall"
  isActiveNow: boolean
  activeUntil: string | null
  nextStartsAt: string | null
  activationId: string | null
  coinAmount: number | null
  dustAmount: number | null
  claimedToday: boolean
}

export interface WorldMapPoint {
  id: string
  kind: WorldMapPointKind
  sourceId: string
  title: string
  lat: number
  lng: number
  radiusM: number
  cityId?: number
  isOwnCity?: boolean
  isAvailable: boolean
  cooldownUntil?: string | null
  dominantLeague?: LocationPoint["dominantLeague"]
  purchasesRemaining?: number
  dailyLimit?: number
  bossLastOutcome?: BossPointView["lastOutcome"]
  history?: PointHistory
  treasure?: WorldMapTreasureMeta
  /** Подпись на маркере карты (HP и т.п.) */
  mapLabel?: string
  worldBossHp?: { current: number; max: number; status: string }
  photos?: string[]
  priceByn?: number
  address?: string | null
  description?: string | null
}

export function formatWorldBossMapHpLabel(
  currentHp: number,
  maxHp: number
): string {
  return `${currentHp.toLocaleString("ru-RU")} / ${maxHp.toLocaleString("ru-RU")} HP`
}

export function scanPointToWorldMap(point: LocationPoint): WorldMapPoint {
  return {
    id: `scan:${point.id}`,
    kind: "scan",
    sourceId: point.id,
    title: point.title,
    lat: point.lat,
    lng: point.lng,
    radiusM: point.radiusM,
    cityId: point.cityId,
    isOwnCity: point.isOwnCity,
    isAvailable: point.isAvailable,
    cooldownUntil: point.cooldownUntil,
    dominantLeague: point.dominantLeague,
    history: point.history,
  }
}

export function shopPointToWorldMap(point: CardShopPoint): WorldMapPoint {
  return {
    id: `shop:${point.id}`,
    kind: "cardshop",
    sourceId: point.id,
    title: point.name,
    lat: point.lat,
    lng: point.lng,
    radiusM: point.radiusM,
    isAvailable: point.purchasesRemaining > 0,
    purchasesRemaining: point.purchasesRemaining,
    dailyLimit: point.dailyLimit,
  }
}

export function boosterShopPointToWorldMap(point: BoosterShopPoint): WorldMapPoint {
  return {
    id: `boostershop:${point.id}`,
    kind: "boostershop",
    sourceId: point.id,
    title: point.name,
    lat: point.latitude,
    lng: point.longitude,
    radiusM: point.radiusMeters,
    cityId: point.cityId,
    isAvailable: true,
    photos: point.photos,
    priceByn: point.priceByn,
    address: point.address,
    description: point.description,
  }
}

export function postOfficePointToWorldMap(point: PostOfficePoint): WorldMapPoint {
  return {
    id: `mail:${point.id}`,
    kind: "postoffice",
    sourceId: point.id,
    title: point.name,
    lat: point.lat,
    lng: point.lng,
    radiusM: point.radiusM,
    cityId: point.cityId,
    isAvailable: true,
  }
}

export function bankPointToWorldMap(point: BankPoint): WorldMapPoint {
  return {
    id: `bank:${point.id}`,
    kind: "bank",
    sourceId: point.id,
    title: point.name,
    lat: point.latitude,
    lng: point.longitude,
    radiusM: point.radiusMeters,
    isAvailable: true,
  }
}

export function cityHallPointToWorldMap(
  point: import("../types/cityHall").CityHallPoint
): WorldMapPoint {
  return {
    id: `cityhall:${point.id}`,
    kind: "cityhall",
    sourceId: point.id,
    title: point.name,
    lat: point.latitude,
    lng: point.longitude,
    radiusM: point.radiusMeters,
    cityId: point.cityId,
    isAvailable: true,
  }
}

export function hangarPointToWorldMap(point: AirshipHangarPoint): WorldMapPoint {
  return {
    id: `hangar:${point.id}`,
    kind: "hangar",
    sourceId: point.id,
    title: point.name,
    lat: point.latitude,
    lng: point.longitude,
    radiusM: point.radiusMeters,
    cityId: point.cityId,
    isOwnCity: true,
    isAvailable: true,
  }
}

export function hotelPointToWorldMap(point: HotelPoint): WorldMapPoint {
  return {
    id: `hotel:${point.id}`,
    kind: "hotel",
    sourceId: point.id,
    title: point.name,
    lat: point.latitude,
    lng: point.longitude,
    radiusM: point.radiusMeters,
    cityId: point.cityId,
    isAvailable: true,
  }
}

export function wiseTreePointToWorldMap(point: WiseTreePoint): WorldMapPoint {
  return {
    id: `wisetree:${point.id}`,
    kind: "wisetree",
    sourceId: point.id,
    title: point.name,
    lat: point.latitude,
    lng: point.longitude,
    radiusM: point.radiusMeters,
    cityId: point.cityId,
    isAvailable: point.occupiedSlots < point.maxSlots,
    mapLabel: `${point.occupiedSlots}/${point.maxSlots}`,
  }
}

export function bossPointToWorldMap(point: BossPointView): WorldMapPoint {
  return {
    id: `boss:${point.id}`,
    kind: "boss",
    sourceId: point.id,
    title: point.name,
    lat: point.latitude,
    lng: point.longitude,
    radiusM: point.radiusMeters,
    isAvailable: point.isAvailable,
    cooldownUntil: point.cooldownUntil,
    bossLastOutcome: point.lastOutcome,
    history: point.history,
  }
}

export function worldBossEventToWorldMap(
  event: WorldBossEventView
): WorldMapPoint {
  const isActive = event.status === "ACTIVE" && event.currentHp > 0
  return {
    id: `worldboss:${event.id}`,
    kind: "worldboss",
    sourceId: event.id,
    title: event.name,
    lat: event.latitude,
    lng: event.longitude,
    radiusM: event.radiusMeters,
    cityId: event.cityId,
    isAvailable: isActive,
    mapLabel: formatWorldBossMapHpLabel(event.currentHp, event.maxHp),
    worldBossHp: {
      current: event.currentHp,
      max: event.maxHp,
      status: event.status,
    },
  }
}

export function treasurePointToWorldMap(
  point: TreasurePoint,
  opts: {
    claimedToday: boolean
    coinAmount?: number | null
    dustAmount?: number | null
  }
): WorldMapPoint {
  const mode = point.kind === "starfall" ? "starfall" : "treasure"
  return {
    id: `${mode}:${point.id}`,
    kind: "treasure",
    sourceId: point.id,
    title: point.name,
    lat: point.latitude,
    lng: point.longitude,
    radiusM: point.radiusMeters,
    cityId: point.cityId,
    isOwnCity: true,
    isAvailable: point.isActiveNow,
    cooldownUntil: point.activeUntil,
    treasure: {
      mode,
      isActiveNow: point.isActiveNow,
      activeUntil: point.activeUntil,
      nextStartsAt: point.nextStartsAt,
      activationId: point.activationId,
      coinAmount: point.coinAmount ?? opts.coinAmount ?? null,
      dustAmount: point.dustAmount ?? opts.dustAmount ?? null,
      claimedToday: mode === "treasure" ? opts.claimedToday : false,
    },
  }
}

export function worldMapPointsToLocationPoints(points: WorldMapPoint[]): LocationPoint[] {
  return points.map((point) => ({
    id: point.id,
    title: point.title,
    lat: point.lat,
    lng: point.lng,
    radiusM: point.radiusM,
    cityId: point.cityId,
    isOwnCity: point.isOwnCity,
    isAvailable: point.isAvailable,
    cooldownUntil: point.cooldownUntil,
    dominantLeague: point.dominantLeague,
    mapKind: point.kind,
    treasureMode: point.treasure?.mode,
    mapLabel: point.mapLabel,
    history: point.history,
  }))
}
